import { create } from 'zustand';
import { useEffect, useState } from 'react';
import { sounds } from '@/audio/synth';
import { useMeta } from '@/state/meta';

interface BootStore {
  done: boolean;
  finish: () => void;
}

export const useBoot = create<BootStore>((set) => ({
  done: useMeta.getState().buffs?.skipBoot ?? false, // honor purchased "skip boot" buff
  finish: () => set({ done: true }),
}));

// Boot variants — picked once per session for variety. Mostly the same shape
// but different middle line for flavor.
const BOOT_VARIANTS: { delay: number; text: string }[][] = [
  [
    { delay: 100, text: 'nasdaq.one' },
    { delay: 500, text: 'connecting to the markets...' },
    { delay: 800, text: 'loading positions...' },
    { delay: 1100, text: 'markets are open.' },
  ],
  [
    { delay: 100, text: 'nasdaq.one' },
    { delay: 500, text: 'restoring last session...' },
    { delay: 800, text: 'reconciling 47 positions you forgot you held...' },
    { delay: 1100, text: 'welcome back.' },
  ],
  [
    { delay: 100, text: 'nasdaq.one' },
    { delay: 500, text: 'authorizing payment-for-order-flow...' },
    { delay: 800, text: 'pre-loading regret...' },
    { delay: 1100, text: 'good luck.' },
  ],
  [
    { delay: 100, text: 'nasdaq.one' },
    { delay: 500, text: 'syncing with prime brokerage that does not exist...' },
    { delay: 800, text: 'compliance has been notified...' },
    { delay: 1100, text: 'open for business.' },
  ],
  [
    { delay: 100, text: 'nasdaq.one' },
    { delay: 500, text: 'pinging Powell. no response...' },
    { delay: 800, text: 'pinging the SEC. one response...' },
    { delay: 1100, text: 'markets are open. SEC is also open.' },
  ],
];

const STAGES = BOOT_VARIANTS[Math.floor(Math.random() * BOOT_VARIANTS.length)]!;

export function BootSequence() {
  const done = useBoot((s) => s.done);
  const finish = useBoot((s) => s.finish);
  const [stage, setStage] = useState(0);

  useEffect(() => {
    if (done) return;
    const timeouts: number[] = [];
    STAGES.forEach((s, i) => {
      timeouts.push(window.setTimeout(() => setStage(i + 1), s.delay));
    });
    timeouts.push(window.setTimeout(() => {
      sounds.boot();
      finish();
    }, 1700));
    return () => {
      timeouts.forEach((t) => window.clearTimeout(t));
    };
  }, [done, finish]);

  // Click / key to skip boot
  useEffect(() => {
    if (done) return;
    function skip() { finish(); }
    window.addEventListener('keydown', skip);
    window.addEventListener('click', skip);
    return () => {
      window.removeEventListener('keydown', skip);
      window.removeEventListener('click', skip);
    };
  }, [done, finish]);

  if (done) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-end justify-start bg-black p-6 sm:p-12 font-mono">
      <div className="space-y-1">
        {STAGES.slice(0, stage).map((s, i) => (
          <div
            key={i}
            className={
              i === 0
                ? 'text-mono-lg text-[var(--accent-primary)] tracking-tight'
                : 'text-mono-sm text-[var(--text-secondary)]'
            }
          >
            <span className="text-[var(--text-muted)]">$ </span>{s.text}
            {i === stage - 1 && (
              <span className="ml-1 inline-block h-3 w-2 animate-pulse bg-[var(--accent-primary)] align-middle" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
